import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useApi } from '../../api/api';
import { useFormContextOptional } from '../../contexts/FormContext';
import { PageDataProvider } from '../PageDataContext';
import type {
  PageDataContextValue,
  PageDataActions,
} from '../PageDataContext';
import { processTimeSeriesData } from '../../utils/fisheriesTimeSeriesProcessor';
import {
  calculateInsights,
  type InsightsResult,
} from '../../utils/fisheriesInsightsCalculator';
import { mapLobsterAnnualResultsToFisheryData } from '../../utils/lobsterFisheryDataMapper';
import {
  aggregateSamsByYear,
  mapSamsToFisheryData,
  buildEconomicsByYear,
} from '../../utils/scallopFisheryDataMapper';
import { DEFAULT_SAMS_ACCESS_CONFIG } from '../../components/AreaAccessConfig/constants';
import { SAMS_DEFAULT_PARAMS } from '../../components/ResetSamsProjection/ResetSamsProjection';
import { LOBSTER_DEFAULT_PARAMS } from '../../components/ResetLobsterProjection/ResetLobsterProjection';
import PageBase from '../../pages/PageBase/PageBase';

export interface FisheryProviderData {
  timeSeriesData: {
    biomass: unknown[];
    impact: unknown[];
    meals: unknown[];
    exvessel: unknown[];
  };
  fisheryData: unknown[];
  years?: number[];
  loading: boolean;
  error: string | null;
  insights: InsightsResult | null;
  originalInsights: InsightsResult | null;
}

type StoredInsights = {
  full: { baseline: InsightsResult | null; adjusted: InsightsResult | null };
  historical: { baseline: InsightsResult | null; adjusted: InsightsResult | null };
  projections: { baseline: InsightsResult | null; adjusted: InsightsResult | null };
};

const defaultStoredInsights: StoredInsights = {
  full: { baseline: null, adjusted: null },
  historical: { baseline: null, adjusted: null },
  projections: { baseline: null, adjusted: null },
};

const defaultTimeSeries = {
  biomass: [] as unknown[],
  impact: [] as unknown[],
  meals: [] as unknown[],
  exvessel: [] as unknown[],
};

export interface StockDataProviderProps {
  regionSlug?: string;
  planSlug?: string;
  stockSlug?: string;
  stockIdForLayout: number | null;
  stockUuidForLayout: string | null;
  stockNameForLayout: string | null;
  stockImageForLayout: string | null;
  topPanelModules: Array<{
    id: string;
    name: string;
    resource_id?: number;
    page_order?: number;
  }> | null;
  selectedModel: string;
}

function formatStockName(name: string): string {
  if (name === 'All Stocks') return name.toLowerCase();
  return name.replace(/-/g, ' in the');
}

const WHAM_COMPATIBLE_STOCK_SLUGS = new Set([
  'redfish',
  'haddock',
  'pollock',
]);

function normalizeStockKey(value: unknown): string {
  return String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function normalizeWhamGroundfishData(response: unknown): Record<string, unknown>[] {
  const data = (response as {
    data?: {
      groundfish_data?: unknown[] | { data?: unknown[] };
      groundfish_results?: Record<string, Record<string, unknown>>;
    };
  })?.data;
  const groundfishData = data?.groundfish_data;

  const flatGroundfishData =
    (Array.isArray(groundfishData)
      ? groundfishData
      : groundfishData &&
          typeof groundfishData === 'object' &&
          Array.isArray((groundfishData as { data?: unknown[] }).data)
        ? (groundfishData as { data: unknown[] }).data
        : null) ?? null;

  if (flatGroundfishData) {
    return flatGroundfishData.filter(
      (item): item is Record<string, unknown> =>
        !!item && typeof item === 'object' && !Array.isArray(item)
    );
  }

  const groundfishResults = data?.groundfish_results;
  if (!groundfishResults || typeof groundfishResults !== 'object') return [];

  return Object.entries(groundfishResults).flatMap(([stockIdStr, yearMap]) =>
    Object.entries(yearMap).map(([yearStr, fields]) => ({
      stock_id: Number(stockIdStr),
      year: Number(yearStr),
      ...(fields as Record<string, unknown>),
    }))
  );
}

function normalizeWhamProjectionPayload(response: unknown): Record<string, unknown> | null {
  const responseData = (response as { data?: Record<string, unknown> })?.data;
  const candidates = [
    responseData?.data,
    responseData?.wham_projection,
    responseData?.wham_output,
    responseData?.projection,
    responseData?.result,
    responseData,
  ];

  for (const candidate of candidates) {
    const parsed =
      typeof candidate === 'string'
        ? (() => {
            try {
              return JSON.parse(candidate) as unknown;
            } catch {
              return null;
            }
          })()
        : candidate;

    if (
      parsed &&
      typeof parsed === 'object' &&
      !Array.isArray(parsed) &&
      Array.isArray((parsed as { years?: unknown[] }).years) &&
      typeof (parsed as { timeseries?: unknown }).timeseries === 'object'
    ) {
      return parsed as Record<string, unknown>;
    }
  }

  return null;
}

function extractBaselineProjectionData(whamData: Record<string, unknown> | null): {
  oflValues: number[];
  projectionYears: number[];
} {
  if (!whamData) return { oflValues: [], projectionYears: [] };

  const nProj = Number((whamData as { n_projection_years?: unknown }).n_projection_years);
  const years = ((whamData as { years?: unknown[] }).years ?? []) as unknown[];
  const timeseries = ((whamData as { timeseries?: Record<string, unknown> }).timeseries ?? {}) as Record<string, unknown>;
  const catchSeries = (timeseries.catch ?? []) as unknown[];

  if (!Number.isFinite(nProj) || nProj <= 0) {
    return { oflValues: [], projectionYears: [] };
  }

  const projectionCount = Math.min(nProj, years.length, catchSeries.length);
  if (projectionCount <= 0) return { oflValues: [], projectionYears: [] };

  return {
    oflValues: catchSeries.slice(-projectionCount).map((value) => Number(value) || 0),
    projectionYears: years.slice(-projectionCount).map((value) => Number(value) || 0),
  };
}

function buildHistoricalSeedDataFromWhamPayload(
  whamData: Record<string, unknown> | null,
  projectionRows: Record<string, unknown>[],
  stockId: number | null,
  stockUuid: string | null
): Record<string, unknown>[] {
  if (!whamData) return [];

  const years = Array.isArray(whamData.years) ? whamData.years : [];
  const timeseries =
    whamData.timeseries && typeof whamData.timeseries === 'object'
      ? (whamData.timeseries as Record<string, unknown>)
      : null;
  const ssbSeriesSource = timeseries?.ssb;
  const catchSeriesSource = timeseries?.catch;
  const ssbSeries = Array.isArray(ssbSeriesSource) ? ssbSeriesSource : [];
  const catchSeries = Array.isArray(catchSeriesSource) ? catchSeriesSource : [];
  if (!years.length || !timeseries || (!ssbSeries.length && !catchSeries.length)) return [];

  const firstProjectionYear = projectionRows.length
    ? Math.min(
        ...projectionRows
          .map((item) => Number(item.year))
          .filter((value) => Number.isFinite(value))
      )
    : Number.POSITIVE_INFINITY;

  const synthesizedRows = years
    .map((yearValue, idx) => {
      const year = Number(yearValue);
      if (!Number.isFinite(year) || year >= firstProjectionYear) return null;

      const ssb = Number(ssbSeries[idx]);
      const catchValue = Number(catchSeries[idx]);
      const normalizedCatch = Number.isFinite(catchValue) ? catchValue : 0;

      return {
        stock_id: stockId ?? undefined,
        stock_uuid: stockUuid ?? undefined,
        year,
        adjusted_biomass: Number.isFinite(ssb) ? ssb : 0,
        original_biomass: Number.isFinite(ssb) ? ssb : 0,
        mean_ofl: normalizedCatch,
        mean_abc: normalizedCatch,
        mean_acl: normalizedCatch,
        landings_value: normalizedCatch,
      };
    })
    .filter((item) => !!item && typeof item === 'object' && !Array.isArray(item));

  return synthesizedRows as Record<string, unknown>[];
}

function buildDefaultWhamProjectionConfig(oflValues: number[]): Record<string, unknown> | null {
  if (!oflValues.length) return null;
  const actualYears = Math.min(oflValues.length, 6);
  const limitedValues = oflValues.slice(0, actualYears);

  return {
    mode: 'OFL',
    mortalityMode: 0,
    projectedCatch: limitedValues,
    abcControlRule: 75,
    utilizationRate: 85,
    whamApiParams: {
      bridge_catch: limitedValues[0] || 0,
      n_yrs: actualYears,
      proj_f_opt: limitedValues.map((_, index) => (index === 0 ? 5 : 3)),
      proj_catch: limitedValues.map((value, index) => (index === 0 ? value : 0)),
    },
  };
}

function buildWhamApiParamsForRequest(
  config: unknown,
  options?: { useExplicitCatchForAllYears?: boolean }
): Record<string, unknown> {
  const typedConfig =
    config && typeof config === 'object' && !Array.isArray(config)
      ? (config as {
          projectedCatch?: unknown[];
          whamApiParams?: {
            bridge_catch?: unknown;
            n_yrs?: unknown;
            proj_f_opt?: unknown[];
            proj_catch?: unknown[];
          };
        })
      : {};

  const baseParams = typedConfig.whamApiParams ?? {};

  if (!options?.useExplicitCatchForAllYears) {
    return { ...baseParams };
  }

  const projectedCatch = Array.isArray(typedConfig.projectedCatch)
    ? typedConfig.projectedCatch.map((value) => Number(value) || 0)
    : [];
  const existingCatch = Array.isArray(baseParams.proj_catch)
    ? baseParams.proj_catch.map((value) => Number(value) || 0)
    : [];
  const existingFOpt = Array.isArray(baseParams.proj_f_opt)
    ? baseParams.proj_f_opt
    : [];
  const derivedYears =
    Number(baseParams.n_yrs) ||
    projectedCatch.length ||
    existingCatch.length ||
    existingFOpt.length;
  const nYears = Math.max(0, Math.min(Number(derivedYears) || 0, 6));
  const explicitCatch = (projectedCatch.length ? projectedCatch : existingCatch)
    .slice(0, nYears)
    .map((value) => Math.round(Number(value) || 0));

  return {
    ...baseParams,
    n_yrs: nYears,
    proj_f_opt: explicitCatch.map(() => 5),
    proj_catch: explicitCatch,
    bridge_catch:
      explicitCatch[0] ??
      (Number(baseParams.bridge_catch) || 0),
  };
}

function buildWhamGroundfishPayload(
  config: unknown,
  managementConfidenceValue: number
): { groundfish?: Record<string, number> } {
  const typedConfig =
    config && typeof config === 'object' && !Array.isArray(config)
      ? (config as {
          abcControlRule?: unknown;
          utilizationRate?: unknown;
        })
      : {};

  const abcControlRule = Number(typedConfig.abcControlRule);
  const utilizationRate = Number(typedConfig.utilizationRate);
  const groundfish: Record<string, number> = {};

  // WHAM downstream payload uses p_star for the Management Confidence slider.
  if (Number.isFinite(managementConfidenceValue)) {
    groundfish.p_star = managementConfidenceValue;
  }

  if (Number.isFinite(abcControlRule)) {
    groundfish.abc_control_rule = abcControlRule / 100;
  }

  if (Number.isFinite(utilizationRate)) {
    groundfish.utilization_rate = utilizationRate / 100;
  }

  return Object.keys(groundfish).length > 0 ? { groundfish } : {};
}

function extractProjectedOflFromFisheryData(fisheryData: Record<string, unknown>[]): {
  oflValues: number[];
  projectionYears: number[];
} {
  const projectionRows = fisheryData
    .filter((item) => Number(item.year) >= 2025)
    .sort((a, b) => Number(a.year) - Number(b.year));

  return {
    oflValues: projectionRows.map((item) => Number(item.mean_ofl) || 0).filter((value) => value > 0),
    projectionYears: projectionRows.map((item) => Number(item.year)).filter((value) => Number.isFinite(value)),
  };
}

function mergeGroundfishSeries(
  existingData: Record<string, unknown>[],
  incomingData: Record<string, unknown>[]
): Record<string, unknown>[] {
  if (!existingData.length) return incomingData;
  if (!incomingData.length) return existingData;

  const mergedByYear = new Map<number, Record<string, unknown>>();

  existingData.forEach((item) => {
    const year = Number(item.year);
    if (Number.isFinite(year)) {
      mergedByYear.set(year, item);
    }
  });

  incomingData.forEach((item) => {
    const year = Number(item.year);
    if (Number.isFinite(year)) {
      mergedByYear.set(year, item);
    }
  });

  return Array.from(mergedByYear.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([, item]) => item);
}

/**
 * StockDataProvider owns all Stock page state, data loading, and actions.
 * Renders PageBase with panelLayoutProps. Does NOT accept pageDataValue or onDataReady.
 */
export function StockDataProvider({
  regionSlug,
  planSlug,
  stockSlug,
  stockIdForLayout,
  stockUuidForLayout,
  stockNameForLayout,
  stockImageForLayout,
  topPanelModules,
  selectedModel,
}: StockDataProviderProps): React.ReactElement {
  const formContext = useFormContextOptional();

  // API (useApi returns JS object; cast for TS when types are not declared)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const api = useApi() as any;
  const {
    getFisheriesDataBatch,
    getYears,
    getMesoscaleDynamicsSettings,
    updateMesoscaleDynamics,
    refreshDatabase,
    getProductivityEnhancementSettings,
    updateProductivityEnhancement,
    updateAllPStarValues,
    updateAllManagementAdjustments,
    updateAllRebuildingTimes,
    updatePStarValue,
    updateManagementAdjustment: apiUpdateManagementAdjustment,
    updateRebuildingTime: apiUpdateRebuildingTime,
    getManagementParameters,
    getStockRebuildingTime,
    getNaturalMortalityParameters,
    updateNaturalMortalityParameters,
    updateAllNaturalMortalityParameters,
    getCasaRuns,
    getCasaAnnualSummary,
    getSamsScenarios,
    getSamsAnnualSummary,
    getSamsAreaSummaryTable,
    getSamsExVesselValue,
    getSamsJobsImpact,
    getSamsMealsImpact,
    getLatestWhamProjection,
    runForecast,
    runWhamProjection,
    getLpueOptions,
    getSpeciesLEK,
    getLobsterScenarios,
    getLobsterScenario,
    getLobsterBaitMarketHistorical,
  } = api;

  // Provider data (fishery time series, insights, etc.)
  const [providerData, setProviderData] = useState<FisheryProviderData>({
    timeSeriesData: { ...defaultTimeSeries },
    fisheryData: [],
    years: [] as number[],
    loading: true,
    error: null,
    insights: null,
    originalInsights: null,
  });

  const refetchRef = useRef<(() => Promise<void>) | undefined>(undefined);
  const captureBaselineRef = useRef<(() => void) | undefined>(undefined);
  const resetInsightsRef = useRef<(() => void) | undefined>(undefined);
  const justUpdatedNaturalMortalityRef = useRef(false);
  const whamBootstrapInFlightRef = useRef(false);

  // Year / range selection
  const [selectedYear, setSelectedYear] = useState('projections');
  const [parametersChanged, setParametersChanged] = useState(false);
  const [isUpdatingParameters, setIsUpdatingParameters] = useState(false);
  const [justRecalculated, setJustRecalculated] = useState(false);

  const [historicalBaitData, setHistoricalBaitData] = useState<unknown[]>([]);

  // P* slider state
  const [pStarValue, setPStarValue] = useState(0.75);
  const [dbPStarValue, setDbPStarValue] = useState(0.75);
  const [updatingPStar, setUpdatingPStar] = useState(false);
  const [recalculationMessage, setRecalculationMessage] = useState('');
  const [pStarUncertaintyValue, setPStarUncertaintyValue] = useState(0.05);
  const [dbPStarUncertaintyValue, setDbPStarUncertaintyValue] = useState(0.05);

  // Management Adjustment
  const [managementAdjValue, setManagementAdjValue] = useState(0.95);
  const [dbManagementAdjValue, setDbManagementAdjValue] = useState(0.95);
  const [managementAdjUncertaintyValue, setManagementAdjUncertaintyValue] =
    useState(0.02);
  const [dbManagementAdjUncertaintyValue, setDbManagementAdjUncertaintyValue] =
    useState(0.02);

  // Rebuilding Time
  const [rebuildingTimeValue, setRebuildingTimeValue] = useState(10);
  const [dbRebuildingTimeValue, setDbRebuildingTimeValue] = useState(10);

  // Natural Mortality
  const [naturalMortalityValue, setNaturalMortalityValue] = useState(0.2);
  const [dbNaturalMortalityValue, setDbNaturalMortalityValue] = useState(0.2);
  const [naturalMortalityUncertaintyValue, setNaturalMortalityUncertaintyValue] =
    useState(0.05);
  const [dbNaturalMortalityUncertaintyValue, setDbNaturalMortalityUncertaintyValue] =
    useState(0.05);
  const [naturalMortalityInitialized, setNaturalMortalityInitialized] = useState(false);

  // Mesoscale Dynamics
  const [mesoscaleDynamicsEnabled, setMesoscaleDynamicsEnabled] = useState(false);
  const [updatingMesoscaleDynamics, setUpdatingMesoscaleDynamics] =
    useState(false);
  const [dbMesoscaleDynamicsEnabled, setDbMesoscaleDynamicsEnabled] =
    useState(false);

  // Zeta
  const [zetaValue, setZetaValue] = useState(1.0);
  const [dbZetaValue, setDbZetaValue] = useState(1.0);
  const [updatingZeta, setUpdatingZeta] = useState(false);

  // Chart / accordion state
  const [selectedChartTab, setSelectedChartTab] = useState('economics');
  const [selectedBasParameterTab, setSelectedBasParameterTab] = useState('natural');
  const [accordionState, setAccordionState] = useState({
    stockAssessmentProjections: false,
    managementParameters: false,
    secondaryManagementFactors: true,
    oceanDynamics: false,
  });
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);

  // Species LEK
  const [showSpeciesLEKData, setShowSpeciesLEKData] = useState(false);
  const [speciesLEKData, setSpeciesLEKData] = useState<unknown>(null);
  const [speciesLEKLoading, setSpeciesLEKLoading] = useState(false);

  // Lobster
  const [lobsterResults, setLobsterResults] = useState<unknown>(null);
  const [lobsterLoading, setLobsterLoading] = useState(false);
  const [lobsterProjectionId, setLobsterProjectionId] = useState<number | null>(
    null
  );
  const [showHerringBaitPriceModal, setShowHerringBaitPriceModal] =
    useState(false);

  // Scallop / SAMS
  const [casaData, setCasaData] = useState<unknown[]>([]);
  const [samsData, setSamsData] = useState<unknown[]>([]);
  const [baselineSamsData, setBaselineSamsData] = useState<unknown[]>([]);
  const [casaRunId, setCasaRunId] = useState<number | null>(null);
  const [rawSamsData, setRawSamsData] = useState<unknown[]>([]);
  const [samsScenarioId, setSamsScenarioId] = useState<number | null>(null);
  const [scallopLoading, setScallopLoading] = useState(false);
  const [scallopMetricSummary, setScallopMetricSummary] = useState<unknown[]>(
    []
  );
  const [baselineScallopMetricSummary, setBaselineScallopMetricSummary] =
    useState<unknown[]>([]);
  const [metricSummaryLoading, setMetricSummaryLoading] = useState(false);
  const [metricSummaryError, setMetricSummaryError] = useState<string | null>(
    null
  );

  // SAMS parameters
  const [samsInputDAS, setSamsInputDAS] = useState(24.0);
  const [dbSamsInputDAS, setDbSamsInputDAS] = useState(24.0);
  const [samsFleetSize, setSamsFleetSize] = useState(300);
  const [dbSamsFleetSize, setDbSamsFleetSize] = useState(300);
  const [samsNYears, setSamsNYears] = useState(3);
  const [dbSamsNYears, setDbSamsNYears] = useState(3);
  const [samsNaturalMortalityGB, setSamsNaturalMortalityGB] = useState(0.2);
  const [dbSamsNaturalMortalityGB, setDbSamsNaturalMortalityGB] = useState(0.2);
  const [samsNaturalMortalityMA, setSamsNaturalMortalityMA] = useState(0.25);
  const [dbSamsNaturalMortalityMA, setDbSamsNaturalMortalityMA] = useState(0.25);
  const [samsLpueMethod, setSamsLpueMethod] = useState('dynamic');
  const [dbSamsLpueMethod, setDbSamsLpueMethod] = useState('dynamic');
  const [lpueOptions, setLpueOptions] = useState<unknown[]>([]);
  const [showSamsAreaConfig, setShowSamsAreaConfig] = useState(false);
  const [dbSamsAccessConfig, setDbSamsAccessConfig] = useState(() => ({
    ...DEFAULT_SAMS_ACCESS_CONFIG,
  }));
  const [samsAccessConfig, setSamsAccessConfig] = useState(() => ({
    ...DEFAULT_SAMS_ACCESS_CONFIG,
  }));
  const [selectedSamsAreas, setSelectedSamsAreas] = useState<string[]>(['all']);
  const [availableSamsAreas, setAvailableSamsAreas] = useState<string[]>([]);
  const samsInitialLoadCompleteRef = useRef(false);

  // WHAM
  const [baselineOflValues, setBaselineOflValues] = useState<number[]>([]);
  const [projectionYears, setProjectionYears] = useState<number[]>([]);
  const [whamProjectionData, setWhamProjectionData] = useState<Record<string, unknown> | null>(null);
  const [whamProjectionConfig, setWhamProjectionConfig] = useState<unknown>(null);

  // Stored insights (baseline vs adjusted)
  const [storedInsights, setStoredInsights] =
    useState<StoredInsights>(defaultStoredInsights);

  const [years, setYears] = useState<number[]>([]);
  const prevSelectionRef = useRef({
    selectedYear: 'full',
    stockIdForLayout: null as number | null,
  });

  // Form context sources (prefer form when available)
  const whamProjectionConfigSource =
    (formContext?.values?.['projection-mortality-strategy'] as unknown) ??
    whamProjectionConfig;
  const naturalMortalityValueSource =
    (formContext?.values?.['groundfish-natural-mortality'] as number) ??
    naturalMortalityValue;
  const pStarValueSource =
    (formContext?.values?.['abc-control-rule'] as number) ?? pStarValue;
  const managementAdjValueSource =
    (formContext?.values?.['mgmt-confidence'] as number) ?? managementAdjValue;
  const samsNaturalMortalityGBSource =
    (formContext?.values?.['scallop-natural-mortality-gb'] as number) ??
    samsNaturalMortalityGB;
  const samsNaturalMortalityMASource =
    (formContext?.values?.['scallop-natural-mortality-ma'] as number) ??
    samsNaturalMortalityMA;
  const samsInputDASSource =
    (formContext?.values?.['days-at-sea'] as number) ?? samsInputDAS;
  const samsFleetSizeSource =
    (formContext?.values?.['fleet-size'] as number) ?? samsFleetSize;
  const samsLpueMethodSource =
    (formContext?.values?.['lpue-calc-method'] as string) ?? samsLpueMethod;
  const samsAccessConfigSource =
    (formContext?.values?.['area-access-config'] as Record<string, unknown>) ??
    samsAccessConfig;
  const selectedYearSource =
    (formContext?.values?.['time-period'] as string) ?? selectedYear;
  const isWhamCompatibleStock =
    selectedModel === 'WHAM' &&
    !!stockSlug &&
    WHAM_COMPATIBLE_STOCK_SLUGS.has(stockSlug);
  const whamStockUuid =
    isWhamCompatibleStock && stockUuidForLayout ? stockUuidForLayout : null;

  const timeSeriesData = providerData.timeSeriesData;
  const fisheryData = providerData.fisheryData as Record<string, unknown>[];
  const yearsFromProvider = (providerData.years ?? []) as number[];
  const error = providerData.error;
  const insights = providerData.insights;
  const originalInsights = providerData.originalInsights;
  const loading =
    selectedModel === 'LOBSTER'
      ? lobsterResults !== null
        ? false
        : lobsterLoading
      : selectedModel === 'CASA/SAMS'
        ? scallopLoading
        : providerData.loading;

  const fisheryDataForInsights = fisheryData;

  const getCurrentPeriodBaseline = useCallback((): InsightsResult | null => {
    switch (selectedYearSource) {
      case 'full':
        return storedInsights.full?.baseline ?? null;
      case 'historical':
        return storedInsights.historical?.baseline ?? null;
      case 'projections':
        return storedInsights.projections?.baseline ?? null;
      default:
        return storedInsights.full?.baseline ?? null;
    }
  }, [selectedYearSource, storedInsights]);

  const getCurrentPeriodAdjusted = useCallback((): InsightsResult | null => {
    switch (selectedYearSource) {
      case 'full':
        return storedInsights.full?.adjusted ?? null;
      case 'historical':
        return storedInsights.historical?.adjusted ?? null;
      case 'projections':
        return storedInsights.projections?.adjusted ?? null;
      default:
        return storedInsights.full?.adjusted ?? null;
    }
  }, [selectedYearSource, storedInsights]);

  const captureBaseline = useCallback(() => {
    if (!fisheryDataForInsights?.length) return;
    const opts = {
      selectedStockId: stockIdForLayout ?? null,
      selectedModel: selectedModel ?? '',
      lobsterResults: lobsterResults as {
        results?: Array<{ fishery_revenue_millions?: number }>;
        annual_results?: Record<string, unknown>[];
      } | null,
    };
    setStoredInsights((prev) => ({
      full: {
        baseline: calculateInsights(fisheryDataForInsights, {
          ...opts,
          forPeriod: 'full',
        }),
        adjusted: prev.full?.adjusted ?? null,
      },
      historical: {
        baseline: calculateInsights(fisheryDataForInsights, {
          ...opts,
          forPeriod: 'historical',
        }),
        adjusted: prev.historical?.adjusted ?? null,
      },
      projections: {
        baseline: calculateInsights(fisheryDataForInsights, {
          ...opts,
          forPeriod: 'projections',
        }),
        adjusted: prev.projections?.adjusted ?? null,
      },
    }));
  }, [fisheryDataForInsights, stockIdForLayout, selectedModel, lobsterResults]);

  const resetInsights = useCallback(() => {
    setStoredInsights(defaultStoredInsights);
  }, []);

  captureBaselineRef.current = captureBaseline;
  resetInsightsRef.current = resetInsights;

  const computeInsightsFromData = useCallback(
    (data: Record<string, unknown>[]) => {
      const opts = {
        selectedStockId: stockIdForLayout ?? null,
        selectedModel: selectedModel ?? '',
        lobsterResults: lobsterResults as {
          results?: Array<{ fishery_revenue_millions?: number }>;
          annual_results?: Record<string, unknown>[];
        } | null,
      };
      return {
        full: calculateInsights(data, { ...opts, forPeriod: 'full' }),
        historical: calculateInsights(data, { ...opts, forPeriod: 'historical' }),
        projections: calculateInsights(data, {
          ...opts,
          forPeriod: 'projections',
        }),
      };
    },
    [stockIdForLayout, selectedModel, lobsterResults]
  );

  const fetchWhamHistoricalSeedData = useCallback(async (
    yearOverride?: number[]
  ): Promise<Record<string, unknown>[]> => {
    const yearsToUse =
      yearOverride?.length
        ? yearOverride
        : years.length
          ? years
          : yearsFromProvider;
    if (!yearsToUse.length || !stockUuidForLayout) return [];

    const response = await getFisheriesDataBatch(yearsToUse, [stockUuidForLayout]);
    const rawData =
      response?.data?.data ?? response?.data?.results ?? response?.data;

    return Array.isArray(rawData) ? (rawData as Record<string, unknown>[]) : [];
  }, [years, yearsFromProvider, stockUuidForLayout, getFisheriesDataBatch]);

  const fetchData = useCallback(async () => {
    const yearsToUse = years.length ? years : yearsFromProvider;
    if (!yearsToUse.length || !stockUuidForLayout) {
      setProviderData((prev) => ({
        ...prev,
        timeSeriesData: defaultTimeSeries,
        fisheryData: [],
        years: yearsToUse,
        loading: false,
        error: null,
        insights: null,
        originalInsights: null,
      }));
      return;
    }

    try {
      setProviderData((prev) => ({
        ...prev,
        timeSeriesData: defaultTimeSeries,
        fisheryData: [],
        years: yearsToUse,
        loading: true,
        error: null,
        insights: null,
        originalInsights: null,
      }));

      const response = await getFisheriesDataBatch(yearsToUse, [
        stockUuidForLayout,
      ]);
      // Support multiple response shapes: { data: { data: [...] } }, { data: { results: [...] } }, or { data: [...] }
      const rawData = response?.data?.data ?? response?.data?.results ?? response?.data;
      const allData = Array.isArray(rawData) ? rawData : [];

      const yearDataMap: Record<
        number,
        { year: number; data: unknown[] }
      > = {};
      yearsToUse.forEach((year) => {
        yearDataMap[year] = { year, data: [] };
      });
      (allData as { year: number }[]).forEach((item) => {
        if (yearDataMap[item.year]) {
          yearDataMap[item.year].data.push(item);
        }
      });

      const allYearData = Object.values(yearDataMap);
      console.log('raw allYearData sample:', allYearData[0]);
      const { biomass, impact, meals, exvessel } = processTimeSeriesData(
        allYearData,
        stockIdForLayout,
        true,
        stockUuidForLayout ?? undefined
      );
      const computed = computeInsightsFromData(
        allData as Record<string, unknown>[]
      );
      const periodInsights =
        (computed as Record<string, InsightsResult | null>)[
          selectedYearSource as string
        ] ?? computed.full ?? computed.historical ?? computed.projections;
      // Update adjusted from new data; preserve baseline when it exists (e.g. after captureBaseline + refetch)
      setStoredInsights((prev) => ({
        full: {
          baseline: prev.full?.baseline ?? computed.full,
          adjusted: computed.full,
        },
        historical: {
          baseline: prev.historical?.baseline ?? computed.historical,
          adjusted: computed.historical,
        },
        projections: {
          baseline: prev.projections?.baseline ?? computed.projections,
          adjusted: computed.projections,
        },
      }));
      setProviderData((prev) => ({
        ...prev,
        timeSeriesData: { biomass, impact, meals, exvessel },
        fisheryData: allData,
        years: yearsToUse,
        loading: false,
        error: null,
        insights: periodInsights,
        originalInsights: periodInsights,
      }));
    } catch (err) {
      console.error('Error fetching fishery data:', err);
      setProviderData((prev) => ({
        ...prev,
        timeSeriesData: defaultTimeSeries,
        fisheryData: [],
        years: yearsToUse,
        loading: false,
        error: 'Failed to load fisheries data. Please try again.',
        insights: null,
        originalInsights: null,
      }));
    }
  }, [
    years,
    yearsFromProvider,
    stockIdForLayout,
    stockUuidForLayout,
    selectedYearSource,
    getFisheriesDataBatch,
    computeInsightsFromData,
  ]);

  refetchRef.current = fetchData;

  // Fetch years on mount
  useEffect(() => {
    let cancelled = false;
    getYears()
      .then((res: { data?: number[] }) => {
        if (!cancelled && res?.data) {
          const sorted = (res.data as number[]).sort((a, b) => a - b);
          setYears(sorted);
          setProviderData((prev) => ({ ...prev, years: sorted }));
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) console.error('Error fetching years:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [getYears]);

  // Fishery data fetch for non-WHAM stocks (skip for LOBSTER/CASA/SAMS, skip when updating)
  useEffect(() => {
    if (
      isUpdatingParameters ||
      selectedModel === 'LOBSTER' ||
      selectedModel === 'CASA/SAMS' ||
      isWhamCompatibleStock
    ) {
      return;
    }
    const yearsToUse = years.length ? years : yearsFromProvider;
    if (!yearsToUse.length || !stockUuidForLayout) return;

    const timeoutId = setTimeout(() => fetchData(), 100);
    return () => clearTimeout(timeoutId);
  // NOTE: isUpdatingParameters is intentionally excluded from deps. The guard at the top of
  // the effect reads the current value to bail out during a recalculation, but we do NOT want
  // the effect to re-fire when isUpdatingParameters returns to false — that would overwrite
  // the run-forecast results with a fresh GET /api/fisheries-data call.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    stockIdForLayout,
    stockUuidForLayout,
    years,
    yearsFromProvider,
    selectedModel,
    isWhamCompatibleStock,
    fetchData,
  ]);

  // Capture adjusted insights when data loads after recalculation
  useEffect(() => {
    if (
      justRecalculated &&
      fisheryDataForInsights.length > 0 &&
      !isUpdatingParameters
    ) {
      const opts = {
        selectedStockId: stockIdForLayout ?? null,
        selectedModel: selectedModel ?? '',
        lobsterResults: lobsterResults as {
          results?: Array<{ fishery_revenue_millions?: number }>;
          annual_results?: Record<string, unknown>[];
        } | null,
      };
      const fullInsights = calculateInsights(fisheryDataForInsights, {
        ...opts,
        forPeriod: 'full',
      });
      const historicalInsights = calculateInsights(fisheryDataForInsights, {
        ...opts,
        forPeriod: 'historical',
      });
      const projectionsInsights = calculateInsights(fisheryDataForInsights, {
        ...opts,
        forPeriod: 'projections',
      });
      setStoredInsights((prev) => ({
        full: { baseline: prev.full?.baseline ?? null, adjusted: fullInsights },
        historical: {
          baseline: prev.historical?.baseline ?? null,
          adjusted: historicalInsights,
        },
        projections: {
          baseline: prev.projections?.baseline ?? null,
          adjusted: projectionsInsights,
        },
      }));
    }
  }, [
    justRecalculated,
    fisheryDataForInsights.length,
    isUpdatingParameters,
    stockIdForLayout,
    selectedModel,
    lobsterResults,
  ]);

  // Lobster: update adjusted insights when lobsterResults changes (e.g. after Run Lobster Projection)
  useEffect(() => {
    if (selectedModel !== 'LOBSTER' || !lobsterResults) return;
    const annualResults = (lobsterResults as { annual_results?: unknown[] })?.annual_results;
    if (!annualResults?.length) return;

    const opts = {
      selectedStockId: null,
      selectedModel: 'LOBSTER',
      lobsterResults: lobsterResults as {
        results?: Array<{ fishery_revenue_millions?: number }>;
        annual_results?: Record<string, unknown>[];
      } | null,
    };
    // calculateInsights for LOBSTER uses lobsterResults from opts; data param must have length
    const fisheryDataForLobster = (providerData.fisheryData ?? []) as Record<string, unknown>[];
    const dataForInsights =
      fisheryDataForLobster.length > 0 ? fisheryDataForLobster : (annualResults as Record<string, unknown>[]);
    const fullInsights = calculateInsights(dataForInsights, { ...opts, forPeriod: 'full' });
    const historicalInsights = calculateInsights(dataForInsights, { ...opts, forPeriod: 'historical' });
    const projectionsInsights = calculateInsights(dataForInsights, { ...opts, forPeriod: 'projections' });

    if (fullInsights) {
      setStoredInsights((prev) => ({
        full: { baseline: prev.full?.baseline ?? fullInsights, adjusted: fullInsights },
        historical: {
          baseline: prev.historical?.baseline ?? historicalInsights,
          adjusted: historicalInsights ?? prev.historical?.adjusted,
        },
        projections: {
          baseline: prev.projections?.baseline ?? projectionsInsights,
          adjusted: projectionsInsights ?? prev.projections?.adjusted,
        },
      }));
    }
  }, [selectedModel, lobsterResults, providerData.fisheryData]);

  // Notify insights when selectedYearSource or storedInsights changes
  useEffect(() => {
    const periodInsights = getCurrentPeriodAdjusted() || getCurrentPeriodBaseline();
    const periodBaseline = getCurrentPeriodBaseline();
    setProviderData((prev) => ({
      ...prev,
      insights: periodInsights ?? null,
      originalInsights: periodBaseline ?? null,
    }));
  }, [selectedYearSource, storedInsights, getCurrentPeriodAdjusted, getCurrentPeriodBaseline]);

  // handleSpeciesLEKLoad
  const handleSpeciesLEKLoad = useCallback(
    async (speciesId: string | number | null) => {
      if (!speciesId) {
        setSpeciesLEKData(null);
        return;
      }
      setSpeciesLEKLoading(true);
      try {
        // API accepts numeric species_id or species_uuid string
        const speciesLEKResult = await api.getSpeciesLEK?.(speciesId);
        if (speciesLEKResult.status === 200 && speciesLEKResult.data) {
          setSpeciesLEKData(speciesLEKResult.data);
        } else {
          setSpeciesLEKData(null);
        }
      } catch (err) {
        console.error('Failed to load species LEK data:', err);
        setSpeciesLEKData(null);
      } finally {
        setSpeciesLEKLoading(false);
      }
    },
    [getSpeciesLEK]
  );

  const handleToggleSpeciesLEKData = useCallback(
    (isActive: boolean, speciesId?: string | number) => {
      setShowSpeciesLEKData(isActive);
      if (isActive && speciesId && !speciesLEKData) {
        handleSpeciesLEKLoad(speciesId);
      } else if (!isActive) {
        setSpeciesLEKData(null);
      }
    },
    [handleSpeciesLEKLoad, speciesLEKData]
  );

  const handleResetSamsToDefaults = useCallback(
    (params?: Partial<typeof SAMS_DEFAULT_PARAMS>) => {
      const cfg = params?.accessConfig ?? DEFAULT_SAMS_ACCESS_CONFIG;
      const inputDAS = params?.inputDAS ?? 24.0;
      const fleetSize = params?.fleetSize ?? 300;
      const nYears = params?.nYears ?? 3;
      const lpueMethod = params?.lpueMethod ?? 'dynamic';

      setSamsInputDAS(inputDAS);
      setSamsFleetSize(fleetSize);
      formContext?.setValue?.('fleet-size', fleetSize);
      setSamsNYears(nYears);
      setDbSamsInputDAS(inputDAS);
      formContext?.setValue?.('days-at-sea', inputDAS);
      setDbSamsFleetSize(fleetSize);
      setDbSamsNYears(nYears);
      setSamsAccessConfig(cfg);
      formContext?.setValue?.('area-access-config', cfg);
      setDbSamsAccessConfig(cfg);
      setBaselineSamsData([]);
      setBaselineScallopMetricSummary([]);
      setSamsLpueMethod(lpueMethod);
      formContext?.setValue?.('lpue-calc-method', lpueMethod);
      setDbSamsLpueMethod(lpueMethod);
    },
    [formContext]
  );

  const handleResetLobsterToDefaults = useCallback(
    (params?: Partial<typeof LOBSTER_DEFAULT_PARAMS>) => {
      const set = (slug: string, dbSlug: string, value: unknown) => {
        formContext?.setValue?.(slug, value);
        formContext?.setValue?.(dbSlug, value);
      };
      set(
        'herring-supply',
        'herring-supply-db',
        params?.herringSupply ?? LOBSTER_DEFAULT_PARAMS.herringSupply
      );
      set(
        'menhaden-supply',
        'menhaden-supply-db',
        params?.menhadenSupply ?? LOBSTER_DEFAULT_PARAMS.menhadenSupply
      );
      set(
        'bait-import-unit-value',
        'bait-import-unit-value-db',
        params?.importUnitValue ?? LOBSTER_DEFAULT_PARAMS.importUnitValue
      );
      formContext?.setValue?.(
        'lobster-projection-years',
        params?.projectionYears ?? LOBSTER_DEFAULT_PARAMS.projectionYears
      );
      formContext?.setValue?.(
        'lobster-projection-years-db',
        params?.projectionYears ?? LOBSTER_DEFAULT_PARAMS.projectionYears
      );
      set('fuel-price', 'fuel-price-db', params?.fuelPrice ?? LOBSTER_DEFAULT_PARAMS.fuelPrice);
      set('trap-price', 'trap-price-db', params?.trapPrice ?? LOBSTER_DEFAULT_PARAMS.trapPrice);
      set('rope-price', 'rope-price-db', params?.ropePrice ?? LOBSTER_DEFAULT_PARAMS.ropePrice);
      formContext?.setValue?.(
        'ropeless-gear-technology',
        params?.ropelessGear ?? LOBSTER_DEFAULT_PARAMS.ropelessGear
      );
      formContext?.setValue?.(
        'ropeless-gear-technology-db',
        params?.ropelessGear ?? LOBSTER_DEFAULT_PARAMS.ropelessGear
      );
    },
    [formContext]
  );

  const handleSamsAreasChange = useCallback((newAreas: string[]) => {
    setSelectedSamsAreas(newAreas);
  }, []);

  const handleModuleSelect = useCallback(
    (moduleId: string | null, accordion?: Record<string, boolean>) => {
      setSelectedModuleId(moduleId);
      if (accordion)
        setAccordionState((prev) => ({ ...prev, ...accordion }));
    },
    []
  );

  const resetParametersChanged = useCallback(() => {
    setParametersChanged(false);
    resetInsightsRef.current?.();
  }, []);

  const applyGroundfishResults = useCallback((
    allData: Record<string, unknown>[],
    options?: { mergeWithExisting?: boolean; baseData?: Record<string, unknown>[] }
  ) => {
    const existingData =
      options?.baseData ??
      ((providerData.fisheryData ?? []) as Record<string, unknown>[]);
    const mergedData =
      options?.mergeWithExisting
        ? mergeGroundfishSeries(
            existingData,
            allData
          )
        : allData;
    const yearsToUse = years.length ? years : yearsFromProvider;
    const yearDataMap: Record<number, { year: number; data: unknown[] }> = {};
    // Initialize with API-fetched years first
    yearsToUse.forEach((yr: number) => { yearDataMap[yr] = { year: yr, data: [] }; });
    // Include all years from mergedData (covers historical WHAM years not in yearsToUse)
    (mergedData as { year: number }[]).forEach((item) => {
      const yr = Number(item.year);
      if (!Number.isFinite(yr)) return;
      if (!yearDataMap[yr]) yearDataMap[yr] = { year: yr, data: [] };
      yearDataMap[yr].data.push(item);
    });
    const allYearData = Object.values(yearDataMap).sort((a, b) => a.year - b.year);
    const { biomass, impact, meals, exvessel } = processTimeSeriesData(
      allYearData,
      stockIdForLayout,
      true,
      stockUuidForLayout ?? undefined
    );
    const computed = computeInsightsFromData(mergedData);
    const periodInsights =
      (computed as Record<string, InsightsResult | null>)[selectedYearSource] ??
      computed.full ?? computed.historical ?? computed.projections;
    setStoredInsights((prev) => ({
      full: { baseline: prev.full?.baseline ?? computed.full, adjusted: computed.full },
      historical: { baseline: prev.historical?.baseline ?? computed.historical, adjusted: computed.historical },
      projections: { baseline: prev.projections?.baseline ?? computed.projections, adjusted: computed.projections },
    }));
    setProviderData((prev) => ({
      ...prev,
      timeSeriesData: { biomass, impact, meals, exvessel },
      fisheryData: mergedData,
      years: yearsToUse,
      loading: false,
      error: null,
      insights: periodInsights,
      originalInsights: periodInsights,
    }));
  }, [
    years,
    yearsFromProvider,
    stockIdForLayout,
    stockUuidForLayout,
    computeInsightsFromData,
    providerData.fisheryData,
    selectedYearSource,
  ]);

  const applyWhamProjectionPayload = useCallback((
    response: unknown,
    options?: { updateBaseline?: boolean }
  ) => {
    const whamData = normalizeWhamProjectionPayload(response);
    setWhamProjectionData(whamData);

    if (options?.updateBaseline) {
      const { oflValues, projectionYears: yearsFromProjection } =
        extractBaselineProjectionData(whamData);
      setBaselineOflValues(oflValues);
      setProjectionYears(yearsFromProjection);
      if (!whamProjectionConfigSource) {
        const defaultConfig = buildDefaultWhamProjectionConfig(oflValues);
        if (defaultConfig) setWhamProjectionConfig(defaultConfig);
      }
    }

    return whamData;
  }, [whamProjectionConfigSource]);

  const handleRunWhamProjection = useCallback(async () => {
    if (
      !isWhamCompatibleStock ||
      !whamProjectionConfigSource ||
      !stockIdForLayout ||
      !whamStockUuid
    ) {
      return;
    }

    setUpdatingPStar(true);
    setIsUpdatingParameters(true);
    setRecalculationMessage('Running WHAM projection...');
    setProviderData((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }));

    try {
      const currentParams = (whamProjectionConfigSource as { whamApiParams?: unknown })
        ?.whamApiParams;
      const hasNaturalMortalityOverride =
        Math.abs(naturalMortalityValueSource - dbNaturalMortalityValue) > 1e-9;
      const useExplicitCatchForAllYears = hasNaturalMortalityOverride;
      const whamApiParams = buildWhamApiParamsForRequest(whamProjectionConfigSource, {
        useExplicitCatchForAllYears,
      });
      const groundfishPayload = buildWhamGroundfishPayload(
        whamProjectionConfigSource,
        managementAdjValueSource
      );
      const naturalMortalityPayload =
        hasNaturalMortalityOverride && Number.isFinite(naturalMortalityValueSource)
          ? {
              natural_mortality: {
                m_new: naturalMortalityValueSource,
              },
            }
          : {};
      const whamResponse = await runWhamProjection(whamStockUuid, {
        ...(currentParams as object),
        ...whamApiParams,
        ...groundfishPayload,
        ...naturalMortalityPayload,
        scenario_name: `${(whamProjectionConfigSource as { mode?: string }).mode} Projection`,
        is_baseline: false,
        notes: `Mode: ${(whamProjectionConfigSource as { mode?: string }).mode}`,
      });
      captureBaselineRef.current?.();
      const whamData = applyWhamProjectionPayload(whamResponse);

      const allData = normalizeWhamGroundfishData(whamResponse);
      if (allData.length) {
        const existingData =
          ((providerData.fisheryData ?? []) as Record<string, unknown>[]);
        const hasHistoricalData = existingData.some((item) => Number(item.year) < 2025);
        if (hasHistoricalData) {
          applyGroundfishResults(allData, {
            mergeWithExisting: true,
            baseData: existingData,
          });
        } else {
          const whamYearsSource = whamData?.years;
          const whamYears = Array.isArray(whamYearsSource)
            ? whamYearsSource
                .map((value) => Number(value))
                .filter((value) => Number.isFinite(value))
            : [];
          const historicalSeedData = await fetchWhamHistoricalSeedData(whamYears);
          const fallbackHistoricalSeedData = buildHistoricalSeedDataFromWhamPayload(
            whamData,
            allData,
            stockIdForLayout,
            stockUuidForLayout
          );
          const usableHistoricalSeedData = historicalSeedData.some(
            (item) => Number(item.year) < 2025
          )
            ? historicalSeedData
            : fallbackHistoricalSeedData;
          applyGroundfishResults(allData, {
            mergeWithExisting: usableHistoricalSeedData.length > 0,
            baseData: usableHistoricalSeedData,
          });
        }
      } else {
        setProviderData((prev) => ({
          ...prev,
          loading: false,
          error: 'WHAM projection returned no data.',
        }));
      }

      setJustRecalculated(true);
      setParametersChanged(true);
      setRecalculationMessage('WHAM projection updated successfully');
      setTimeout(() => setRecalculationMessage(''), 3000);
    } catch (projectionError) {
      console.error('WHAM projection failed:', projectionError);
      setProviderData((prev) => ({
        ...prev,
        loading: false,
        error: 'WHAM projection failed. Please try again.',
      }));
      setRecalculationMessage('');
    } finally {
      setUpdatingPStar(false);
      setIsUpdatingParameters(false);
    }
  }, [
    isWhamCompatibleStock,
    whamProjectionConfigSource,
    stockIdForLayout,
    whamStockUuid,
    naturalMortalityValueSource,
    dbNaturalMortalityValue,
    managementAdjValueSource,
    providerData.fisheryData,
    fetchWhamHistoricalSeedData,
    runWhamProjection,
    applyWhamProjectionPayload,
    applyGroundfishResults,
  ]);

  const handleRunForecast = useCallback(async () => {
    const changedParameters: Record<string, unknown> = {};

    if (
      pStarValueSource !== dbPStarValue ||
      pStarUncertaintyValue !== dbPStarUncertaintyValue
    ) {
      changedParameters.pstar = {
        value: pStarValueSource,
        uncertainty: pStarUncertaintyValue,
      };
    }
  
    if (
      managementAdjValueSource !== dbManagementAdjValue ||
      managementAdjUncertaintyValue !== dbManagementAdjUncertaintyValue
    ) {
      changedParameters.managementAdjustment = {
        value: managementAdjValueSource,
        uncertainty: managementAdjUncertaintyValue,
      };
    }
  
    if (rebuildingTimeValue !== dbRebuildingTimeValue) {
      changedParameters.rebuildingTime = { value: rebuildingTimeValue };
    }
  
    if (
      naturalMortalityValueSource !== dbNaturalMortalityValue ||
      naturalMortalityUncertaintyValue !== dbNaturalMortalityUncertaintyValue
    ) {
      changedParameters.naturalMortality = {
        value: naturalMortalityValueSource,
        uncertainty: naturalMortalityUncertaintyValue,
      };
    }
  
    if (zetaValue !== dbZetaValue) {
      changedParameters.productivityEnhancement = { value: zetaValue };
    }
  
    if (mesoscaleDynamicsEnabled !== dbMesoscaleDynamicsEnabled) {
      changedParameters.mesoscaleDynamics = { value: mesoscaleDynamicsEnabled };
    }

    if (Object.keys(changedParameters).length === 0) {
      return;
    }

    setUpdatingPStar(true);
    setIsUpdatingParameters(true);
    setRecalculationMessage('Running forecast...');

    try {
      captureBaselineRef.current?.();

      if (changedParameters.naturalMortality) {
        formContext?.setValue?.('groundfish-natural-mortality', naturalMortalityValueSource);
        justUpdatedNaturalMortalityRef.current = true;
      }

      if (changedParameters.productivityEnhancement) {
        await updateProductivityEnhancement({ zeta: zetaValue });
        setDbZetaValue(zetaValue);
      }

      if (changedParameters.mesoscaleDynamics) {
        await updateMesoscaleDynamics({ enabled: mesoscaleDynamicsEnabled });
        setDbMesoscaleDynamicsEnabled(mesoscaleDynamicsEnabled);
      }

      setParametersChanged(true);
      setProviderData((prev) => ({
        ...prev,
        fisheryData: [],
        timeSeriesData: defaultTimeSeries,
        loading: true,
        error: null,
      }));

      const yearsToUse = years.length ? years : yearsFromProvider;
      type PerStockOverride = {
        baseline_m: number;
        alpha: number;
        m_uncertainty: number;
        alpha_uncertainty: number;
        p_star: number;
        p_star_uncertainty: number;
        management_adjustment: number;
        adjustment_uncertainty: number;
        t_rebuild: number;
      };
      const forecastBody: {
        stock_uuids: string[];
        years: number[];
        parameters?: Record<string, PerStockOverride>;
      } = {
        stock_uuids: stockUuidForLayout ? [stockUuidForLayout] : [],
        years: yearsToUse,
      };

      if (stockIdForLayout !== null) {
        forecastBody.parameters = {
          [String(stockIdForLayout)]: {
            baseline_m: naturalMortalityValueSource,
            alpha: 0.8,
            m_uncertainty: naturalMortalityUncertaintyValue,
            alpha_uncertainty: naturalMortalityUncertaintyValue,
            p_star: pStarValueSource,
            p_star_uncertainty: pStarUncertaintyValue,
            management_adjustment: managementAdjValueSource,
            adjustment_uncertainty: managementAdjUncertaintyValue,
            t_rebuild: rebuildingTimeValue,
          },
        };
      }

      const forecastResponse = await runForecast(forecastBody);
      const rawForecastData =
        forecastResponse?.data?.data ??
        forecastResponse?.data?.results ??
        forecastResponse?.data;
      const allData = Array.isArray(rawForecastData) ? rawForecastData : [];

      if (allData.length) {
        applyGroundfishResults(allData as Record<string, unknown>[]);
      } else {
        setProviderData((prev) => ({
          ...prev,
          loading: false,
          error: 'Forecast returned no data.',
        }));
      }

      setRecalculationMessage('Forecast updated successfully');
      setTimeout(() => setRecalculationMessage(''), 3000);
      setJustRecalculated(true);
    } catch (err) {
      console.error('Error during forecast update:', err);
      setProviderData((prev) => ({
        ...prev,
        loading: false,
        error: 'Failed to update parameters. Please try again.',
      }));
      setRecalculationMessage('');
    } finally {
      setUpdatingPStar(false);
      setIsUpdatingParameters(false);
    }
  }, [
    pStarValueSource,
    dbPStarValue,
    pStarUncertaintyValue,
    dbPStarUncertaintyValue,
    managementAdjValueSource,
    dbManagementAdjValue,
    managementAdjUncertaintyValue,
    dbManagementAdjUncertaintyValue,
    rebuildingTimeValue,
    dbRebuildingTimeValue,
    naturalMortalityValueSource,
    dbNaturalMortalityValue,
    naturalMortalityUncertaintyValue,
    dbNaturalMortalityUncertaintyValue,
    zetaValue,
    dbZetaValue,
    mesoscaleDynamicsEnabled,
    dbMesoscaleDynamicsEnabled,
    stockIdForLayout,
    stockUuidForLayout,
    years,
    yearsFromProvider,
    applyGroundfishResults,
    updateProductivityEnhancement,
    updateMesoscaleDynamics,
    runForecast,
  ]);

  const handleOnSamsProjectionComplete = useCallback(
    (payload: {
      scenarioId?: number;
      samsData?: unknown[];
      rawSamsData?: unknown[];
      baselineSamsData?: unknown[];
      fisheryData?: unknown[];
      scallopMetricSummary?: unknown[];
      baselineScallopMetricSummary?: unknown[];
      dbParams?: {
        inputDAS?: number;
        fleetSize?: number;
        nYears?: number;
        accessConfig?: Record<string, unknown>;
        lpueMethod?: string;
      };
    }) => {
      const {
        scenarioId,
        samsData: newSamsData,
        rawSamsData: newRawSamsData,
        baselineSamsData: newBaselineSams,
        fisheryData: newFisheryData,
        scallopMetricSummary: newMetrics,
        baselineScallopMetricSummary: newBaselineMetrics,
        dbParams,
      } = payload;

      if (scenarioId != null) setSamsScenarioId(scenarioId);
      if (newSamsData) setSamsData(newSamsData);
      if (newRawSamsData) setRawSamsData(newRawSamsData);
      if (newBaselineSams?.length) setBaselineSamsData(newBaselineSams);
      if (newFisheryData) {
        const fd = newFisheryData as Array<Record<string, unknown>>;
        const fullInsights = calculateInsights(fd, {
          forPeriod: 'full',
          selectedStockId: null,
          selectedModel: 'CASA/SAMS',
        });
        const historicalInsights = calculateInsights(fd, {
          forPeriod: 'historical',
          selectedStockId: null,
          selectedModel: 'CASA/SAMS',
        });
        const projectionsInsights = calculateInsights(fd, {
          forPeriod: 'projections',
          selectedStockId: null,
          selectedModel: 'CASA/SAMS',
        });
        setStoredInsights((prev) => ({
          full: { baseline: prev.full?.baseline ?? null, adjusted: fullInsights },
          historical: { baseline: prev.historical?.baseline ?? null, adjusted: historicalInsights },
          projections: { baseline: prev.projections?.baseline ?? null, adjusted: projectionsInsights },
        }));
        const exvessel = fd.map((r) => {
          const actual = Number(r.mean_ex_vessel) || 0;
          const unc = Number(r.ex_vessel_uncertainty) || 0;
          return { year: r.year, actual, potential: actual, actualUncertainty: unc, potentialUncertainty: unc };
        });
        const meals = fd.map((r) => {
          const actual = Number(r.meals_produced) || 0;
          const unc = Number(r.meals_uncertainty) || 0;
          return { year: r.year, actual, potential: actual, actualUncertainty: unc, potentialUncertainty: unc };
        });
        setProviderData((prev) => ({
          ...prev,
          fisheryData: newFisheryData,
          timeSeriesData: {
            ...prev.timeSeriesData,
            exvessel,
            meals,
          },
        }));
      }
      if (newMetrics?.length) setScallopMetricSummary(newMetrics);
      if (newBaselineMetrics?.length)
        setBaselineScallopMetricSummary(newBaselineMetrics);
      samsInitialLoadCompleteRef.current = true;

      if (dbParams) {
        if (dbParams.inputDAS != null) setDbSamsInputDAS(dbParams.inputDAS);
        if (dbParams.fleetSize != null) setDbSamsFleetSize(dbParams.fleetSize);
        if (dbParams.nYears != null) setDbSamsNYears(dbParams.nYears);
        if (dbParams.accessConfig)
          setDbSamsAccessConfig(dbParams.accessConfig as typeof DEFAULT_SAMS_ACCESS_CONFIG);
        if (dbParams.lpueMethod) setDbSamsLpueMethod(dbParams.lpueMethod);
      }
      setParametersChanged(true);
      setJustRecalculated(true);
    },
    []
  );

  const handleOnLobsterProjectionComplete = useCallback(
    (payload: {
      results?: unknown;
      projectionId?: number;
      fisheryData?: unknown[];
      dbParams?: Record<string, unknown>;
    }) => {
      const { results, projectionId, fisheryData: newFisheryData, dbParams } =
        payload;
      if (results) setLobsterResults(results);
      if (projectionId != null) setLobsterProjectionId(projectionId);
      if (newFisheryData)
        setProviderData((prev) => ({ ...prev, fisheryData: newFisheryData }));

      if (dbParams && formContext) {
        if (dbParams.herringSupply)
          formContext.setValue('herring-supply-db', dbParams.herringSupply);
        if (dbParams.menhadenSupply)
          formContext.setValue('menhaden-supply-db', dbParams.menhadenSupply);
        if (dbParams.importUnitValue !== undefined)
          formContext.setValue('bait-import-unit-value-db', dbParams.importUnitValue);
        if (dbParams.fuelPrice !== undefined)
          formContext.setValue('fuel-price-db', dbParams.fuelPrice);
        if (dbParams.trapPrice !== undefined)
          formContext.setValue('trap-price-db', dbParams.trapPrice);
        if (dbParams.ropePrice !== undefined)
          formContext.setValue('rope-price-db', dbParams.ropePrice);
        if (dbParams.ropelessGear !== undefined)
          formContext.setValue('ropeless-gear-technology-db', dbParams.ropelessGear);
      }
      setParametersChanged(true);
      setJustRecalculated(true);
    },
    [formContext]
  );

  const handleRefreshDatabase = useCallback(async () => {
    if (
      !window.confirm(
        'This will clear all data and rebuild the database from 2015 to 2030. This operation may take several minutes. Are you sure you want to proceed?'
      )
    ) {
      return;
    }
    setUpdatingPStar(true);
    setRecalculationMessage(
      'Refreshing database, please wait... This may take several minutes.'
    );
    try {
      await refreshDatabase();
      setRecalculationMessage('Database refresh completed. Reloading data...');
      resetParametersChanged();
      setJustRecalculated(false);
      if (refetchRef.current) await refetchRef.current();
      setRecalculationMessage('Database refreshed successfully!');
      setTimeout(() => setRecalculationMessage(''), 3000);
    } catch (err) {
      console.error('Error refreshing database:', err);
      setProviderData((prev) => ({
        ...prev,
        error: 'Failed to refresh database. Please try again or contact support.',
      }));
      setRecalculationMessage('');
    } finally {
      setUpdatingPStar(false);
    }
  }, [refreshDatabase, resetParametersChanged]);

  // Lobster data load
  useEffect(() => {
    console.log('[LOBSTER StockDataProvider] useEffect', { selectedModel, willLoad: selectedModel === 'LOBSTER' });
    if (selectedModel !== 'LOBSTER') return;

    const loadLobsterData = async () => {
      console.log('[LOBSTER StockDataProvider] loadLobsterData starting');
      try {
        setLobsterLoading(true);
        setProviderData((prev) => ({ ...prev, loading: true }));

        try {
          console.log('[LOBSTER StockDataProvider] calling getLobsterBaitMarketHistorical');
          const baitResponse = await getLobsterBaitMarketHistorical('four_regime_v1');
          console.log('[LOBSTER StockDataProvider] getLobsterBaitMarketHistorical done', { hasData: !!baitResponse?.data?.data });
          if (baitResponse?.data?.data) {
            setHistoricalBaitData(baitResponse.data.data as unknown[]);
          }
        } catch (baitErr) {
          console.warn('[LOBSTER StockDataProvider] getLobsterBaitMarketHistorical failed (non-fatal)', baitErr);
        }

        const response = await (getLobsterScenarios as (limit?: number | null) => Promise<{ data?: unknown; results?: unknown[] }>)(10);
        let scenarios: unknown[] = [];
        if (Array.isArray(response?.data)) {
          scenarios = response.data;
        } else if (response?.data && typeof response.data === 'object' && Array.isArray((response.data as { results?: unknown[] }).results)) {
          scenarios = (response.data as { results: unknown[] }).results;
        }
        if (scenarios.length > 0) {
          const latestScenario = scenarios[0] as { id: number };
          setLobsterProjectionId(latestScenario.id);
          console.log('[LOBSTER StockDataProvider] calling getLobsterScenario', { id: latestScenario.id });
          const scenarioData = await getLobsterScenario(latestScenario.id);
          console.log('[LOBSTER StockDataProvider] getLobsterScenario result', { hasData: !!scenarioData?.data, hasAnnualResults: !!scenarioData?.data?.annual_results });

          if (scenarioData.data?.params && formContext) {
            const params = scenarioData.data.params as Record<string, unknown>;
            const set = (slug: string, dbSlug: string, value: unknown) => {
              formContext.setValue(slug, value);
              formContext.setValue(dbSlug, value);
            };
            const baitMarket = params.bait_market as Record<string, unknown> | undefined;
            if (baitMarket) {
              set('herring-supply', 'herring-supply-db', baitMarket.herring_supply_mt);
              set('menhaden-supply', 'menhaden-supply-db', baitMarket.menhaden_supply_mt);
              set('bait-import-unit-value', 'bait-import-unit-value-db', baitMarket.import_unit_value);
            }
            const opex = params.opex as Record<string, unknown> | undefined;
            if (opex) {
              set('fuel-price', 'fuel-price-db', opex.fuel_price_per_gal);
            }
            const gear = params.gear as Record<string, unknown> | undefined;
            if (gear) {
              set('trap-price', 'trap-price-db', gear.trap_price);
              set('rope-price', 'rope-price-db', gear.rope_price_per_ft);
              formContext.setValue('ropeless-gear-technology', gear.ropeless);
              formContext.setValue('ropeless-gear-technology-db', gear.ropeless);
            }
            const sim = params.simulation as { years?: number } | undefined;
            if (sim?.years != null) {
              formContext.setValue('lobster-projection-years', sim.years);
              formContext.setValue('lobster-projection-years-db', sim.years);
            }
          }

          if (scenarioData.data?.annual_results) {
            const lobsterFisheryData = mapLobsterAnnualResultsToFisheryData(
              scenarioData.data.annual_results as Parameters<typeof mapLobsterAnnualResultsToFisheryData>[0]
            );
            const lobsterDataAsRecords = lobsterFisheryData as unknown as Record<string, unknown>[];
            const opts = {
              selectedStockId: null,
              selectedModel: 'LOBSTER',
              lobsterResults: scenarioData.data as {
                results?: Array<{ fishery_revenue_millions?: number }>;
                annual_results?: Record<string, unknown>[];
              } | null,
            };
            const fullInsights = calculateInsights(lobsterDataAsRecords, { ...opts, forPeriod: 'full' });
            const historicalInsights = calculateInsights(lobsterDataAsRecords, { ...opts, forPeriod: 'historical' });
            const projectionsInsights = calculateInsights(lobsterDataAsRecords, { ...opts, forPeriod: 'projections' });
            setStoredInsights({
              full: { baseline: fullInsights, adjusted: fullInsights },
              historical: { baseline: historicalInsights, adjusted: historicalInsights },
              projections: { baseline: projectionsInsights, adjusted: projectionsInsights },
            });
            setProviderData((prev) => ({
              ...prev,
              fisheryData: lobsterFisheryData,
              insights: fullInsights,
              originalInsights: fullInsights,
            }));
          }

          setLobsterResults(scenarioData.data);
        } else {
          console.log('[LOBSTER StockDataProvider] no scenarios found, clearing data');
          setLobsterResults(null);
          setStoredInsights(defaultStoredInsights);
          setProviderData((prev) => ({ ...prev, fisheryData: [], insights: null, originalInsights: null }));
        }
      } catch (err) {
        console.error('[LOBSTER StockDataProvider] Error loading lobster data:', err);
        setLobsterResults(null);
        setProviderData((prev) => ({
          ...prev,
          fisheryData: [],
          error: 'Failed to load lobster data',
        }));
      } finally {
        setLobsterLoading(false);
        setProviderData((prev) => ({ ...prev, loading: false }));
      }
    };
    loadLobsterData();
    // formContext intentionally excluded: re-running when user edits form would overwrite with API values
  }, [selectedModel, getLobsterScenarios, getLobsterScenario, getLobsterBaitMarketHistorical]);

  // Scallop data load
  useEffect(() => {
    if (selectedModel !== 'CASA/SAMS') return;

    const loadScallopData = async () => {
      try {
        setScallopLoading(true);
        setCasaData([]);
        setCasaRunId(null);

        const samsScenariosResponse = await getSamsScenarios(null, 10);
        let samsScenarios: { scenario_id: number }[] = [];

        if (samsScenariosResponse?.data?.success && samsScenariosResponse?.data?.results) {
          samsScenarios = samsScenariosResponse.data.results;
        } else if (Array.isArray(samsScenariosResponse?.data)) {
          samsScenarios = samsScenariosResponse.data;
        }

        if (samsScenarios.length === 0) {
          setScallopLoading(false);
          return;
        }

        const latestSamsScenario = samsScenarios[0];
        setSamsScenarioId(latestSamsScenario.scenario_id);

        const samsSummaryResponse = await getSamsAnnualSummary(
          latestSamsScenario.scenario_id
        );
        let samsSummary: Record<string, unknown>[] = [];
        let exVesselData: Record<string, unknown>[] = [];
        let jobsData: Record<string, unknown>[] = [];
        let mealsData: Record<string, unknown>[] = [];

        try {
          const exVesselResponse = await getSamsExVesselValue(
            latestSamsScenario.scenario_id
          );
          if (exVesselResponse?.data) {
            exVesselData = Array.isArray(exVesselResponse.data)
              ? exVesselResponse.data
              : (exVesselResponse.data as { success?: boolean; results?: unknown[] })?.results ?? [];
          }
        } catch {
          /* ignore */
        }
        try {
          const jobsResponse = await getSamsJobsImpact(
            latestSamsScenario.scenario_id
          );
          if (jobsResponse?.data) {
            jobsData = Array.isArray(jobsResponse.data)
              ? jobsResponse.data
              : (jobsResponse.data as { success?: boolean; results?: unknown[] })?.results ?? [];
          }
        } catch {
          /* ignore */
        }
        try {
          const mealsResponse = await getSamsMealsImpact(
            latestSamsScenario.scenario_id
          );
          if (mealsResponse?.data) {
            mealsData = Array.isArray(mealsResponse.data)
              ? mealsResponse.data
              : (mealsResponse.data as { success?: boolean; results?: unknown[] })?.results ?? [];
          }
        } catch {
          /* ignore */
        }

        if (samsSummaryResponse?.data?.success && samsSummaryResponse?.data?.results) {
          samsSummary = samsSummaryResponse.data.results;
        } else if (Array.isArray(samsSummaryResponse?.data)) {
          samsSummary = samsSummaryResponse.data;
        }

        setRawSamsData(samsSummary);
        if (!baselineSamsData || (baselineSamsData as unknown[]).length === 0) {
          setBaselineSamsData(samsSummary);
        }

        const aggregatedSams = aggregateSamsByYear(
          samsSummary as Array<{ year?: number; sams_area?: string; total_abundance?: number; total_biomass_mt?: number; exploitable_biomass_mt?: number }>
        );
        setSamsData(aggregatedSams);

        const uniqueAreas = [
          ...new Set(
            samsSummary.map((row) => row.sams_area as string).filter(Boolean)
          ),
        ].sort();
        setAvailableSamsAreas(uniqueAreas);

        const economicsByYear = buildEconomicsByYear(
          exVesselData,
          jobsData,
          mealsData
        );
        const scallopFisheryData = mapSamsToFisheryData(
          aggregatedSams as Parameters<typeof mapSamsToFisheryData>[0],
          economicsByYear
        );

        const scallopFisheryDataAsRecords = scallopFisheryData as unknown as Record<string, unknown>[];
        const scallopInsightsFull = calculateInsights(scallopFisheryDataAsRecords, {
          forPeriod: 'full',
          selectedStockId: null,
          selectedModel: 'CASA/SAMS',
        });
        const scallopInsightsHistorical = calculateInsights(scallopFisheryDataAsRecords, {
          forPeriod: 'historical',
          selectedStockId: null,
          selectedModel: 'CASA/SAMS',
        });
        const scallopInsightsProjections = calculateInsights(scallopFisheryDataAsRecords, {
          forPeriod: 'projections',
          selectedStockId: null,
          selectedModel: 'CASA/SAMS',
        });
        setStoredInsights({
          full: { baseline: scallopInsightsFull, adjusted: scallopInsightsFull },
          historical: { baseline: scallopInsightsHistorical, adjusted: scallopInsightsHistorical },
          projections: { baseline: scallopInsightsProjections, adjusted: scallopInsightsProjections },
        });

        const exvessel = scallopFisheryData.map((r) => {
          const actual = Number((r as { mean_ex_vessel?: number }).mean_ex_vessel) || 0;
          const unc = Number((r as { ex_vessel_uncertainty?: number }).ex_vessel_uncertainty) || 0;
          return {
            year: (r as { year?: number }).year,
            actual,
            potential: actual,
            actualUncertainty: unc,
            potentialUncertainty: unc,
          };
        });
        const meals = scallopFisheryData.map((r) => {
          const actual = Number((r as { meals_produced?: number }).meals_produced) || 0;
          const unc = Number((r as { meals_uncertainty?: number }).meals_uncertainty) || 0;
          return {
            year: (r as { year?: number }).year,
            actual,
            potential: actual,
            actualUncertainty: unc,
            potentialUncertainty: unc,
          };
        });

        setProviderData((prev) => ({
          ...prev,
          fisheryData: scallopFisheryData,
          insights: scallopInsightsFull,
          originalInsights: scallopInsightsFull,
          timeSeriesData: {
            ...prev.timeSeriesData,
            exvessel,
            meals,
          },
        }));
        samsInitialLoadCompleteRef.current = true;
      } catch (err) {
        console.error('Error loading scallop data:', err);
        setProviderData((prev) => ({
          ...prev,
          error: 'Failed to load scallop data. Please try again.',
        }));
      } finally {
        setScallopLoading(false);
      }
    };
    loadScallopData();
  }, [selectedModel, getSamsScenarios, getSamsAnnualSummary, getSamsExVesselValue, getSamsJobsImpact, getSamsMealsImpact]);

  // WHAM baseline bootstrap
  useEffect(() => {
    if (!stockIdForLayout || !whamStockUuid || !isWhamCompatibleStock) {
      setBaselineOflValues([]);
      setProjectionYears([]);
      setWhamProjectionData(null);
      whamBootstrapInFlightRef.current = false;
      return;
    }
    if (!naturalMortalityInitialized) return;
    if (baselineOflValues.length > 0 && whamProjectionData) return;
    if (whamBootstrapInFlightRef.current) return;

    let cancelled = false;

    const fetchWhamBaseline = async () => {
      whamBootstrapInFlightRef.current = true;
      try {
        setProviderData((prev) => ({
          ...prev,
          loading: true,
          error: null,
        }));

        let bootstrapConfig =
          (whamProjectionConfigSource as Record<string, unknown> | null) ?? null;

        if (!bootstrapConfig) {
          const latestProjectionResponse = await getLatestWhamProjection(whamStockUuid);
          if (cancelled) return;
          const latestWhamData = normalizeWhamProjectionPayload(latestProjectionResponse);
          const latestProjection = extractBaselineProjectionData(latestWhamData);

          if (!cancelled && latestProjection.oflValues.length > 0) {
            setBaselineOflValues(latestProjection.oflValues);
            setProjectionYears(latestProjection.projectionYears);
          }

          bootstrapConfig = buildDefaultWhamProjectionConfig(latestProjection.oflValues);
        }

        if (!bootstrapConfig) {
          const yearsToUse = years.length ? years : yearsFromProvider;
          if (yearsToUse.length && stockUuidForLayout) {
            const response = await getFisheriesDataBatch(yearsToUse, [stockUuidForLayout]);
            if (cancelled) return;
            const rawData =
              response?.data?.data ?? response?.data?.results ?? response?.data;
            const allData = Array.isArray(rawData) ? rawData : [];
            const extracted = extractProjectedOflFromFisheryData(
              allData as Record<string, unknown>[]
            );
            if (!cancelled && extracted.oflValues.length > 0) {
              setBaselineOflValues(extracted.oflValues);
              setProjectionYears(extracted.projectionYears);
            }
            bootstrapConfig = buildDefaultWhamProjectionConfig(extracted.oflValues);
          }
        }

        if (!bootstrapConfig) {
          if (!cancelled) {
            setBaselineOflValues([]);
            setProjectionYears([]);
            setWhamProjectionData(null);
            setProviderData((prev) => ({
              ...prev,
              loading: false,
            }));
          }
          return;
        }

        if (!cancelled && !whamProjectionConfigSource) {
          setWhamProjectionConfig(bootstrapConfig);
        }

        const whamApiParams = buildWhamApiParamsForRequest(bootstrapConfig, {
          useExplicitCatchForAllYears: false,
        });
        const groundfishPayload = buildWhamGroundfishPayload(
          bootstrapConfig,
          managementAdjValueSource
        );
        const baselineResponse = await runWhamProjection(whamStockUuid, {
          ...whamApiParams,
          ...groundfishPayload,
          scenario_name: `${String((bootstrapConfig as { mode?: unknown }).mode ?? 'OFL')} Projection`,
          is_baseline: false,
          notes: `Mode: ${String((bootstrapConfig as { mode?: unknown }).mode ?? 'OFL')}`,
        });
        if (cancelled) return;
        const baselineWhamData = applyWhamProjectionPayload(baselineResponse, {
          updateBaseline: true,
        });
        const baselineGroundfishData = normalizeWhamGroundfishData(baselineResponse);
        if (baselineGroundfishData.length > 0) {
          const baselineWhamYearsSource = baselineWhamData?.years;
          const whamYears = Array.isArray(baselineWhamYearsSource)
            ? baselineWhamYearsSource
                .map((value) => Number(value))
                .filter((value) => Number.isFinite(value))
            : [];
          const historicalSeedData = await fetchWhamHistoricalSeedData(whamYears);
          const fallbackHistoricalSeedData = buildHistoricalSeedDataFromWhamPayload(
            baselineWhamData,
            baselineGroundfishData,
            stockIdForLayout,
            stockUuidForLayout
          );
          const usableHistoricalSeedData = historicalSeedData.some(
            (item) => Number(item.year) < 2025
          )
            ? historicalSeedData
            : fallbackHistoricalSeedData;
          if (cancelled) return;
          applyGroundfishResults(baselineGroundfishData, {
            mergeWithExisting: usableHistoricalSeedData.length > 0,
            baseData: usableHistoricalSeedData,
          });
        } else if (!cancelled) {
          setProviderData((prev) => ({
            ...prev,
            loading: false,
          }));
        }
      } catch (err) {
        console.error('Error fetching baseline OFL:', err);
        if (!cancelled) {
          setBaselineOflValues([]);
          setProjectionYears([]);
          setWhamProjectionData(null);
          setProviderData((prev) => ({
            ...prev,
            loading: false,
            error: 'Failed to load WHAM baseline projection.',
          }));
        }
      } finally {
        if (!cancelled) {
          whamBootstrapInFlightRef.current = false;
        }
      }
    };
    fetchWhamBaseline();
    return () => {
      cancelled = true;
      whamBootstrapInFlightRef.current = false;
    };
  }, [
    stockIdForLayout,
    whamStockUuid,
    isWhamCompatibleStock,
    naturalMortalityInitialized,
    naturalMortalityValueSource,
    managementAdjValueSource,
    years,
    yearsFromProvider,
    stockUuidForLayout,
    fetchWhamHistoricalSeedData,
    getFisheriesDataBatch,
    getLatestWhamProjection,
    runWhamProjection,
    applyWhamProjectionPayload,
    applyGroundfishResults,
  ]);

  // SAMS metric summary
  useEffect(() => {
    if (selectedModel !== 'CASA/SAMS' || !samsScenarioId) return;

    const loadMetricSummary = async () => {
      try {
        setMetricSummaryLoading(true);
        setMetricSummaryError(null);
        const response = await getSamsAreaSummaryTable(samsScenarioId);
        let summaryData: unknown[] = [];
        if (response?.data?.success && response?.data?.area_summary) {
          summaryData = response.data.area_summary;
        } else if (response?.data?.area_summary) {
          summaryData = response.data.area_summary;
        }
        setScallopMetricSummary(summaryData);
        if (
          !baselineScallopMetricSummary ||
          (baselineScallopMetricSummary as unknown[]).length === 0
        ) {
          setBaselineScallopMetricSummary(summaryData);
        }
      } catch (err) {
        console.error('Error loading metric summary:', err);
        setMetricSummaryError('Failed to load metric summary');
      } finally {
        setMetricSummaryLoading(false);
      }
    };
    loadMetricSummary();
  }, [
    samsScenarioId,
    selectedModel,
    getSamsAreaSummaryTable,
    baselineScallopMetricSummary,
  ]);

  // LPUE options
  useEffect(() => {
    if (selectedModel !== 'CASA/SAMS') return;

    const loadLpueOptions = async () => {
      try {
        const response = await getLpueOptions();
        const optionsData = (response?.data || []) as unknown[];
        setLpueOptions(optionsData);
        if (!samsLpueMethod && optionsData.length > 0) {
          const defaultOpt = optionsData.find(
            (o: unknown) => (o as { default?: boolean }).default
          ) as { method_key?: string } | undefined;
          const defaultMethod =
            defaultOpt?.method_key ?? (optionsData[0] as string);
          setSamsLpueMethod(defaultMethod);
          setDbSamsLpueMethod(defaultMethod);
        }
      } catch {
        setLpueOptions(['dynamic', 'fixed_1502', 'fixed_2285']);
        if (!samsLpueMethod) {
          setSamsLpueMethod('dynamic');
          setDbSamsLpueMethod('dynamic');
        }
      }
    };
    loadLpueOptions();
  }, [selectedModel, getLpueOptions, samsLpueMethod]);

  // SAMS areas change - re-aggregate
  useEffect(() => {
    if (selectedModel !== 'CASA/SAMS') return;
    if (!rawSamsData || (rawSamsData as unknown[]).length === 0) return;
    if (!samsInitialLoadCompleteRef.current) return;

    const aggregatedSams = aggregateSamsByYear(
      rawSamsData as Array<{ year?: number; sams_area?: string; total_abundance?: number; total_biomass_mt?: number; exploitable_biomass_mt?: number }>,
      selectedSamsAreas
    );
    setSamsData(aggregatedSams);

    setProviderData((prev) => {
      const prevFisheryData = (prev.fisheryData || []) as Record<string, unknown>[];
      const historicalData = prevFisheryData.filter((d) => (d.year as number) <= 2024);
      const existingProjectionData = prevFisheryData.filter((d) => (d.year as number) >= 2025);
      const existingByYear: Record<number, Record<string, unknown>> = {};
      existingProjectionData.forEach((item) => {
        existingByYear[item.year as number] = item;
      });

      const projectionData = aggregatedSams.map((row: { year: number }) => {
        const existing = existingByYear[row.year];
        if (existing) {
          return {
            ...existing,
            year: row.year,
            stock_id: null,
            stock_name: 'Atlantic Scallop',
          };
        }
        return {
          year: row.year,
          stock_id: null,
          stock_name: 'Atlantic Scallop',
          mean_ex_vessel: 0,
          ex_vessel_uncertainty: 0,
          landed_economic_impact: 0,
          landed_economic_impact_uncertainty: 0,
          missedEconomyValue: 0,
          missedEconomyUncertainty: 0,
          mean_uncaught_ex_vessel: 0,
          uncaught_ex_vessel_uncertainty: 0,
          meals_produced: 0,
          meals_uncertainty: 0,
          trade_deficit_contribution: 0,
          trade_deficit_uncertainty: 0,
          total_jobs_mean: 0,
          total_jobs_uncertainty: 0,
          harvesting_jobs_mean: 0,
          processing_jobs_mean: 0,
          distribution_jobs_mean: 0,
          support_services_jobs_mean: 0,
          induced_jobs_mean: 0,
          mean_uncaught_acl: 0,
          uncaught_acl_uncertainty: 0,
        };
      });
      return { ...prev, fisheryData: [...historicalData, ...projectionData] };
    });
  }, [selectedSamsAreas, selectedModel, rawSamsData]);

  // Management parameters fetch
  useEffect(() => {
    if (isUpdatingParameters || justRecalculated) return;

    const fetchAllManagementParameters = async () => {
      try {
        if (stockIdForLayout === null) {
          setPStarValue(0.75);
          setDbPStarValue(0.75);
          formContext?.setValue?.('abc-control-rule', 0.75);
          setPStarUncertaintyValue(0.05);
          setDbPStarUncertaintyValue(0.05);
          setManagementAdjValue(0.95);
          setDbManagementAdjValue(0.95);
          formContext?.setValue?.('mgmt-confidence', 0.95);
          setManagementAdjUncertaintyValue(0.02);
          setDbManagementAdjUncertaintyValue(0.02);
          return;
        }

        const response = await getManagementParameters();
        const rawParams = response?.data?.results ?? response?.data?.data ?? response?.data;
        const params = Array.isArray(rawParams)
          ? rawParams
          : rawParams && typeof rawParams === 'object' && !Array.isArray(rawParams)
            ? [rawParams]
            : [];
        const stockParams = params.find((p: Record<string, unknown>) => {
          if (stockUuidForLayout && p.stock_uuid != null) {
            return String(p.stock_uuid) === String(stockUuidForLayout);
          }
          const pStockId = p.stock_id ?? p.fishery_management_stock_id;
          return (
            pStockId != null &&
            stockIdForLayout != null &&
            Number(pStockId) === Number(stockIdForLayout)
          );
        });

        // In the management parameters fetch useEffect, replace the stockParams block:

        if (stockParams) {
          if (stockParams.p_star !== undefined) {
            // ✅ Always sync both live and db values from API on stock load.
            // The effect is already guarded at the top with:
            //   if (isUpdatingParameters || justRecalculated) return;
            // so we never overwrite in-flight user edits.
            setPStarValue(stockParams.p_star);
            setDbPStarValue(stockParams.p_star);
            formContext?.setValue?.('abc-control-rule', stockParams.p_star);
          }
          if (stockParams.p_star_uncertainty !== undefined) {
            setPStarUncertaintyValue(stockParams.p_star_uncertainty);
            setDbPStarUncertaintyValue(stockParams.p_star_uncertainty);
          }
          if (stockParams.management_adjustment !== undefined) {
            setManagementAdjValue(stockParams.management_adjustment);
            setDbManagementAdjValue(stockParams.management_adjustment);
            formContext?.setValue?.('mgmt-confidence', stockParams.management_adjustment);
          }
          if (stockParams.adjustment_uncertainty !== undefined) {
            setManagementAdjUncertaintyValue(stockParams.adjustment_uncertainty);
            setDbManagementAdjUncertaintyValue(stockParams.adjustment_uncertainty);
          }
        } else {
          // No params found for this stock — reset to defaults
          setPStarValue(0.75);
          setDbPStarValue(0.75);
          formContext?.setValue?.('abc-control-rule', 0.75);
          setPStarUncertaintyValue(0.05);
          setDbPStarUncertaintyValue(0.05);
          setManagementAdjValue(0.95);
          setDbManagementAdjValue(0.95);
          formContext?.setValue?.('mgmt-confidence', 0.95);
          setManagementAdjUncertaintyValue(0.02);
          setDbManagementAdjUncertaintyValue(0.02);
        }
      } catch (err) {
        console.error('Error fetching management parameters:', err);
        setPStarValue((p) => p || 0.75);
        setDbPStarValue((p) => p || 0.75);
        formContext?.setValue?.('abc-control-rule', 0.75);
        setManagementAdjValue((p) => p || 0.95);
        setDbManagementAdjValue((p) => p || 0.95);
        formContext?.setValue?.('mgmt-confidence', 0.95);
      }
    };
    fetchAllManagementParameters();
    // formContext intentionally excluded: re-running when user edits form would overwrite with API values.
    // justRecalculated is intentionally excluded: flipping it back to false after a projection should not
    // re-fetch baseline parameters and reset slider form values.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockIdForLayout, stockUuidForLayout, isUpdatingParameters, getManagementParameters]);

  // Rebuilding time fetch
  useEffect(() => {
    if (isUpdatingParameters || justRecalculated) return;
    if (stockIdForLayout === null) {
      setRebuildingTimeValue(10);
      setDbRebuildingTimeValue(10);
      return;
    }

    const fetchRebuildingTime = async () => {
      try {
        const response = await getStockRebuildingTime(stockUuidForLayout!);
        if (response.data?.rebuilding_time_years !== undefined) {
          setRebuildingTimeValue(response.data.rebuilding_time_years);
          setDbRebuildingTimeValue(response.data.rebuilding_time_years);
        } else {
          setRebuildingTimeValue(10);
          setDbRebuildingTimeValue(10);
        }
      } catch {
        setRebuildingTimeValue(10);
        setDbRebuildingTimeValue(10);
      }
    };
    fetchRebuildingTime();
    // justRecalculated is intentionally excluded: finishing a projection should not pull baseline
    // rebuilding settings back over the user's current inputs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockIdForLayout, stockUuidForLayout, isUpdatingParameters, getStockRebuildingTime]);

  // Natural mortality fetch
  useEffect(() => {
    setNaturalMortalityInitialized(false);
  }, [stockIdForLayout, stockUuidForLayout]);

  useEffect(() => {
    if (isUpdatingParameters || justRecalculated) return;
    if (justUpdatedNaturalMortalityRef.current) {
      justUpdatedNaturalMortalityRef.current = false;
      return; // Skip refetch - we just updated and set the form value in the forecast submit handler
    }
    if (stockIdForLayout === null) {
      setNaturalMortalityValue(0.2);
      setDbNaturalMortalityValue(0.2);
      formContext?.setValue?.('groundfish-natural-mortality', 0.2);
      setNaturalMortalityInitialized(true);
      return;
    }

    const fetchNaturalMortality = async () => {
      try {
        const response = await getNaturalMortalityParameters();
        // Support multiple response shapes: { data: [...] }, { data: { results: [...] } }, { data: { data: [...] } }, or single object
        const rawParams =
          response?.data?.results ??
          response?.data?.data ??
          response?.data?.parameters ??
          response?.data;
        const params = Array.isArray(rawParams)
          ? rawParams
          : rawParams && typeof rawParams === 'object' && !Array.isArray(rawParams)
            ? [rawParams]
            : [];
        const stockParams = params.find((p: Record<string, unknown>) => {
          if (stockUuidForLayout && p.stock_uuid != null) {
            return String(p.stock_uuid) === String(stockUuidForLayout);
          }
          const pStockId = p.stock_id ?? p.fishery_management_stock_id;
          return (
            pStockId != null &&
            stockIdForLayout != null &&
            Number(pStockId) === Number(stockIdForLayout)
          );
        });
        const sp = stockParams as Record<string, unknown> | undefined;
        // Always use m_new_value for the Natural Mortality (Mnew) slider; fallback to baseline only when m_new_value is null
        const mNewValue = sp?.m_new_value ?? (sp as Record<string, unknown> | undefined)?.mNewValue;
        const baseline = sp?.baseline_natural_mortality ?? (sp as Record<string, unknown> | undefined)?.baselineNaturalMortality;
        const valueToUse = mNewValue != null ? (mNewValue as number) : (baseline != null ? (baseline as number) : 0.2);
        setNaturalMortalityValue(valueToUse);
        setDbNaturalMortalityValue(valueToUse);
        formContext?.setValue?.('groundfish-natural-mortality', valueToUse);
        setNaturalMortalityInitialized(true);
      } catch {
        setNaturalMortalityValue(0.2);
        setDbNaturalMortalityValue(0.2);
        formContext?.setValue?.('groundfish-natural-mortality', 0.2);
        setNaturalMortalityInitialized(true);
      }
    };
    fetchNaturalMortality();
    // formContext intentionally excluded: re-running when user edits form would overwrite with API values.
    // justRecalculated is intentionally excluded: flipping it back to false after a projection should not
    // re-fetch baseline natural mortality and reset the slider form value.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockIdForLayout, stockUuidForLayout, isUpdatingParameters, getNaturalMortalityParameters]);

  // Natural mortality uncertainty fetch
  useEffect(() => {
    if (isUpdatingParameters || justRecalculated || stockIdForLayout === null)
      return;

    const fetchUncertainty = async () => {
      try {
        const response = await getNaturalMortalityParameters();
        const rawParams =
          response?.data?.results ??
          response?.data?.data ??
          response?.data?.parameters ??
          response?.data;
        const params = Array.isArray(rawParams)
          ? rawParams
          : rawParams && typeof rawParams === 'object' && !Array.isArray(rawParams)
            ? [rawParams]
            : [];
        const stockParams = params.find((p: Record<string, unknown>) => {
          if (stockUuidForLayout && p.stock_uuid != null) {
            return String(p.stock_uuid) === String(stockUuidForLayout);
          }
          const pStockId = p.stock_id ?? p.fishery_management_stock_id;
          return (
            pStockId != null &&
            stockIdForLayout != null &&
            Number(pStockId) === Number(stockIdForLayout)
          );
        });
        const mUncertainty = stockParams && (stockParams as Record<string, unknown>).m_uncertainty;
        if (mUncertainty != null) {
          setNaturalMortalityUncertaintyValue(mUncertainty as number);
          setDbNaturalMortalityUncertaintyValue(mUncertainty as number);
        } else {
          setNaturalMortalityUncertaintyValue(0.05);
          setDbNaturalMortalityUncertaintyValue(0.05);
        }
      } catch {
        setNaturalMortalityUncertaintyValue(0.05);
        setDbNaturalMortalityUncertaintyValue(0.05);
      }
    };
    fetchUncertainty();
    // justRecalculated is intentionally excluded: finishing a projection should not immediately
    // overwrite adjusted uncertainty state with baseline API values.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockIdForLayout, stockUuidForLayout, isUpdatingParameters, getNaturalMortalityParameters]);

  // Mesoscale and zeta settings on mount
  useEffect(() => {
    getMesoscaleDynamicsSettings()
      .then((res: { data: { enabled?: boolean } }) => {
        const enabled = res.data.enabled ?? false;
        setMesoscaleDynamicsEnabled(enabled);
        setDbMesoscaleDynamicsEnabled(enabled);
      })
      .catch(() => {
        setMesoscaleDynamicsEnabled(false);
        setDbMesoscaleDynamicsEnabled(false);
      });
    getProductivityEnhancementSettings()
      .then((res: { data: { zeta?: number } }) => {
        const zeta = res.data.zeta ?? 1.0;
        setZetaValue(zeta);
        setDbZetaValue(zeta);
      })
      .catch(() => {
        setZetaValue(1.0);
        setDbZetaValue(1.0);
      });
  }, [getMesoscaleDynamicsSettings, getProductivityEnhancementSettings]);

  // Reset justRecalculated when insights updated
  useEffect(() => {
    if (justRecalculated && insights && fisheryData.length > 0) {
      setJustRecalculated(false);
    }
  }, [justRecalculated, insights, fisheryData.length]);

  // Reset parameters when stock changes
  useEffect(() => {
    const currentSelection = {
      selectedYear: selectedYearSource,
      stockIdForLayout,
    };
    const prevSelection = prevSelectionRef.current;
    const stockChanged = prevSelection.stockIdForLayout !== stockIdForLayout;
    const yearChanged = prevSelection.selectedYear !== selectedYearSource;

    if (stockChanged) {
      resetParametersChanged();
      if (justRecalculated) setJustRecalculated(false);
      whamBootstrapInFlightRef.current = false;
      setWhamProjectionConfig(null);
      setWhamProjectionData(null);
      setBaselineOflValues([]);
      setProjectionYears([]);
      setProviderData((prev) => ({
        ...prev,
        timeSeriesData: defaultTimeSeries,
        fisheryData: [],
        loading: true,
        error: null,
        insights: null,
        originalInsights: null,
      }));
    } else if (yearChanged && justRecalculated) {
      setJustRecalculated(false);
    }
    prevSelectionRef.current = currentSelection;
  }, [selectedYearSource, stockIdForLayout, resetParametersChanged, justRecalculated]);

  const getSelectedStockName = () => {
    if (stockIdForLayout === null) return 'All Stocks';
    return stockNameForLayout ?? 'Unknown Stock';
  };

  const fisheryStockId = useMemo(() => {
    if (!fisheryData?.length) return null;
    const ids = [...new Set(fisheryData.map((r) => Number((r as { stock_id?: number })?.stock_id)).filter((n) => !Number.isNaN(n) && n > 0))];
    return ids.length === 1 ? ids[0] : null;
  }, [fisheryData]);

  const pageDataValue: PageDataContextValue = useMemo(
    () => ({
      data: {
        timeSeriesData,
        insights: insights || originalInsights,
        originalInsights,
        parametersChanged,
        stockName: formatStockName(getSelectedStockName()),
        stockId: stockIdForLayout,
        fisheryStockId,
        stockUuid: stockUuidForLayout,
        selectedYear: selectedYearSource,
        fisheryData,
        loading,
        error,
        lobsterResults,
        lobsterLoading,
        scallopMetricSummary,
        baselineScallopMetricSummary,
        metricSummaryLoading,
        metricSummaryError,
        rawSamsData,
        baselineSamsData,
        scallopLoading,
        casaRunId,
        casaData,
        samsInputDAS: samsInputDASSource,
        samsFleetSize: samsFleetSizeSource,
        samsNYears,
        samsAccessConfig: samsAccessConfigSource,
        samsLpueMethod: samsLpueMethodSource,
        samsNaturalMortalityGB: samsNaturalMortalityGBSource,
        samsNaturalMortalityMA: samsNaturalMortalityMASource,
        lpueOptions,
        dbPStarValue,
        dbManagementAdjValue,
        dbRebuildingTimeValue,
        dbNaturalMortalityValue,
        updatingPStar,
        stockIdForLayout,
        stockSlug,
        selectedModel,
        selectedSamsAreas,
        availableSamsAreas,
        baselineOflValues,
        projectionYears,
        whamProjectionData,
        isWhamCompatibleStock,
      },
      loading,
      error,
      submit: isWhamCompatibleStock ? handleRunForecast : undefined,
      actions: Object.fromEntries(
        Object.entries({
          openOflAbcModal:
            isWhamCompatibleStock
              ? () => window.dispatchEvent(new CustomEvent('openOflAbcModal'))
              : undefined,
          openStockAssessmentReportModal:
            isWhamCompatibleStock &&
            stockSlug === 'redfish'
              ? () =>
                  window.dispatchEvent(
                    new CustomEvent('openStockAssessmentReportModal')
                  )
              : undefined,
          openScallopBiomassModal:
            selectedModel === 'CASA/SAMS'
              ? () =>
                  window.dispatchEvent(
                    new CustomEvent('openScallopBiomassModal')
                  )
              : undefined,
          openScallopMetricsModal:
            selectedModel === 'CASA/SAMS'
              ? () =>
                  window.dispatchEvent(
                    new CustomEvent('openScallopMetricsModal')
                  )
              : undefined,
          openLobsterExpenseModal:
            selectedModel === 'LOBSTER'
              ? () =>
                  window.dispatchEvent(
                    new CustomEvent('openLobsterExpenseModal')
                  )
              : undefined,
          openHerringBaitPriceModal:
            selectedModel === 'LOBSTER'
              ? () => setShowHerringBaitPriceModal(true)
              : undefined,
          closeHerringBaitPriceModal: () =>
            setShowHerringBaitPriceModal(false),
          openMealsModal:
            isWhamCompatibleStock
              ? () => window.dispatchEvent(new CustomEvent('openMealsModal'))
              : undefined,
          openEconomicImpactModal:
            isWhamCompatibleStock
              ? () =>
                  window.dispatchEvent(
                    new CustomEvent('openEconomicImpactModal')
                  )
              : undefined,
          runGroundfishProjection: handleRunForecast,
          runWhamProjection: handleRunWhamProjection,
          refreshDatabase: handleRefreshDatabase,
          resetSamsProjection: () =>
            handleResetSamsToDefaults(SAMS_DEFAULT_PARAMS),
          onResetSamsToDefaults: handleResetSamsToDefaults,
          resetLobsterProjection: () =>
            handleResetLobsterToDefaults(LOBSTER_DEFAULT_PARAMS),
          onResetLobsterToDefaults: handleResetLobsterToDefaults,
          captureBaseline: () => captureBaselineRef.current?.(),
          setLobsterLoading,
          setUpdatingPStar,
          setRecalculationMessage,
          setProviderData,
          onLobsterProjectionComplete: handleOnLobsterProjectionComplete,
          onSamsProjectionComplete: handleOnSamsProjectionComplete,
          onAreasChange: handleSamsAreasChange,
        }).filter(([, v]) => typeof v === 'function')
      ) as PageDataActions,
    }),
    [
      timeSeriesData,
      insights,
      originalInsights,
      parametersChanged,
      stockIdForLayout,
      fisheryStockId,
      stockUuidForLayout,
      stockSlug,
      selectedYearSource,
      fisheryData,
      loading,
      error,
      lobsterResults,
      lobsterLoading,
      scallopMetricSummary,
      baselineScallopMetricSummary,
      metricSummaryLoading,
      metricSummaryError,
      rawSamsData,
      baselineSamsData,
      scallopLoading,
      casaRunId,
      casaData,
      samsInputDASSource,
      samsFleetSizeSource,
      samsNYears,
      samsAccessConfigSource,
      samsLpueMethodSource,
      samsNaturalMortalityGBSource,
      samsNaturalMortalityMASource,
      lpueOptions,
      dbPStarValue,
      dbManagementAdjValue,
      dbRebuildingTimeValue,
      dbNaturalMortalityValue,
      updatingPStar,
      selectedModel,
      isWhamCompatibleStock,
      selectedSamsAreas,
      availableSamsAreas,
      baselineOflValues,
      projectionYears,
      whamProjectionData,
      handleRunForecast,
      handleRunWhamProjection,
      handleRefreshDatabase,
      handleResetSamsToDefaults,
      handleResetLobsterToDefaults,
      handleOnLobsterProjectionComplete,
      handleOnSamsProjectionComplete,
      handleSamsAreasChange,
    ]
  );

  return (
    <PageDataProvider value={pageDataValue}>
      <PageBase
        pageType="stock"
        resourceId={stockIdForLayout}
        leftPanelTitle="Fishery Management Adjustments"
        rightPanelTitle={
          showSpeciesLEKData && speciesLEKData
            ? 'Local Ecological Knowledge'
            : 'Fishery Performance'
        }
        topPanelModules={topPanelModules ?? undefined}
        panelLayoutProps={{
          onSpeciesLEKLoad: handleSpeciesLEKLoad,
          onToggleSpeciesLEKData: handleToggleSpeciesLEKData,
          speciesLEKData,
          onModuleSelect: handleModuleSelect,
          selectedModuleId,
          stockImageUrl: stockImageForLayout,
          leftPanelInjections: {
            oflValues: baselineOflValues,
            projectionYears,
            disabled: updatingPStar || loading,
            fuelPriceUpdatingParams: updatingPStar || lobsterLoading,
            lobsterDisabled: updatingPStar,
            naturalMortalityValue,
            dbNaturalMortalityValue,
            pStarValue,
            dbPStarValue,
            managementAdjValue,
            dbManagementAdjValue,
            samsNaturalMortalityGB,
            dbSamsNaturalMortalityGB,
            samsNaturalMortalityMA,
            dbSamsNaturalMortalityMA,
            samsInputDAS,
            dbSamsInputDAS,
            samsFleetSize,
            dbSamsFleetSize,
            samsLpueMethod,
            dbSamsLpueMethod,
            lpueOptions,
            samsAccessConfig,
            dbSamsAccessConfig,
          },
          rightPanelInjections: {
            stockId: stockIdForLayout,
            fisheryStockId,
            stockUuid: stockUuidForLayout,
            selectedYear: selectedYearSource,
            loading,
            speciesLEKData,
            speciesLEKLoading,
            defaultTimePeriod: selectedYearSource,
            insights: insights || originalInsights,
            originalInsights,
            parametersChanged,
            stockName: formatStockName(getSelectedStockName()),
            lobsterLoading,
            showStatusQuoColumn: selectedYearSource !== 'historical',
            showSingleColumn: selectedYearSource === 'historical',
            showChangeIndicators:
              selectedYearSource !== 'historical' &&
              !!originalInsights &&
              parametersChanged,
            selectedSamsAreas,
            availableSamsAreas,
            onAreasChange: handleSamsAreasChange,
            showAreaFilter: selectedModel === 'CASA/SAMS',
            scallopMetricSummary,
          },
        }}
      />
    </PageDataProvider>
  );
}
